import type { GameState, SaveMeta, ImportResult, SharedStarterKit, SharedPlayerProfile } from './persistence-contract.js'
import type { WorldGalleryEntry, VesselGalleryEntry } from './types.js'
import { getAppImportMetaEnv } from './env.js'
import type { CreatorAnalyticsSnapshot } from './creator-analytics.js'

type RequestOptions = {
  method?: string
  body?: unknown
  headers?: Record<string, string>
}

const env = getAppImportMetaEnv()

export function getBackendBaseUrl(): string {
  const configured = String(env.VITE_BACKEND_BASE_URL ?? '').trim()
  return configured ? configured.replace(/\/+$/, '') : '/api/backend'
}

async function backendJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(`${getBackendBaseUrl()}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  const text = await response.text()
  const payload = text ? JSON.parse(text) : {}
  if (!response.ok) {
    const message = typeof payload?.error === 'string'
      ? payload.error
      : typeof payload?.message === 'string'
        ? payload.message
        : `Backend request failed (${response.status})`
    throw new Error(message)
  }
  return payload as T
}

export async function checkWhitelist(uid: string): Promise<boolean> {
  const result = await backendJson<{ whitelisted: boolean }>('/whitelist/check', {
    method: 'POST',
    body: { uid },
  })
  return !!result.whitelisted
}

export async function loadAdminConfig(ids: string[]): Promise<Record<string, unknown>> {
  const result = await backendJson<{ configs: Record<string, unknown> }>('/config/read', {
    method: 'POST',
    body: { ids },
  })
  return result.configs ?? {}
}

export async function saveAdminConfig(id: string, data: unknown): Promise<void> {
  await backendJson('/config/write', {
    method: 'POST',
    body: { id, data },
  })
}

export async function listPublicWorlds(): Promise<any[]> {
  const result = await backendJson<{ worlds: any[] }>('/worlds')
  return result.worlds ?? []
}

export async function listCreatorWorlds(creatorUid: string): Promise<any[]> {
  const result = await backendJson<{ worlds: any[] }>(`/worlds?creatorUid=${encodeURIComponent(String(creatorUid || '').trim())}`)
  return result.worlds ?? []
}

export async function loadCreatorAnalytics(creatorUid: string): Promise<CreatorAnalyticsSnapshot> {
  const result = await backendJson<CreatorAnalyticsSnapshot>(`/creator/analytics?creatorUid=${encodeURIComponent(String(creatorUid || '').trim())}`)
  return result
}

export async function saveMarketplaceWorld(manifest: unknown): Promise<void> {
  await backendJson('/marketplace/write', {
    method: 'POST',
    body: { manifest },
  })
}

export async function listSaves(uid: string): Promise<SaveMeta[]> {
  const result = await backendJson<{ saves: SaveMeta[] }>('/saves/list', {
    method: 'POST',
    body: { uid },
  })
  return result.saves ?? []
}

export async function loadSave(uid: string, saveId: string): Promise<GameState | null> {
  const result = await backendJson<{ state: GameState | null }>('/saves/load', {
    method: 'POST',
    body: { uid, saveId },
  })
  return result.state ?? null
}

export async function writeSave(uid: string, saveId: string, state: GameState): Promise<void> {
  await backendJson('/saves/write', {
    method: 'POST',
    body: { uid, saveId, state },
  })
}

export async function deleteSave(uid: string, saveId: string): Promise<void> {
  await backendJson('/saves/delete', {
    method: 'POST',
    body: { uid, saveId },
  })
}

export async function readRemoteMetaTimestamp(uid: string, saveId: string): Promise<{ exists: boolean; timestamp: number }> {
  const result = await backendJson<{ exists: boolean; timestamp: number }>('/saves/meta', {
    method: 'POST',
    body: { uid, saveId },
  })
  return result
}

export async function importArtifactByCode(code: string): Promise<ImportResult> {
  const result = await backendJson<{ result: ImportResult }>('/artifacts/import', {
    method: 'POST',
    body: { code },
  })
  return result.result
}

export async function exportArtifact(code: string, payload: string, type: 'blueprint' | 'vessel' | 'starter' | 'scenario'): Promise<void> {
  await backendJson('/artifacts/export', {
    method: 'POST',
    body: { code, payload, type },
  })
}

export async function loadLogicCache(uid: string, saveId: string): Promise<Record<string, unknown> | null> {
  const result = await backendJson<{ data: Record<string, unknown> | null }>('/logic-cache/load', {
    method: 'POST',
    body: { uid, saveId },
  })
  return result.data ?? null
}

export async function writeLogicCache(uid: string, saveId: string, data: Record<string, unknown>): Promise<void> {
  await backendJson('/logic-cache/write', {
    method: 'POST',
    body: { uid, saveId, data },
  })
}

export async function loadNpcLogicCache(uid: string, key: string): Promise<unknown | null> {
  const result = await backendJson<{ data: unknown | null }>('/npc-logic-cache/load', {
    method: 'POST',
    body: { uid, key },
  })
  return result.data ?? null
}

export async function writeNpcLogicCache(uid: string, key: string, data: unknown): Promise<void> {
  await backendJson('/npc-logic-cache/write', {
    method: 'POST',
    body: { uid, key, data },
  })
}

export async function uploadSaveImage(uid: string, saveId: string, imageId: string, base64: string): Promise<string> {
  const result = await backendJson<{ publicUrl: string }>('/media/upload', {
    method: 'POST',
    body: { uid, saveId, imageId, base64 },
  })
  return result.publicUrl
}

export async function deleteSaveMedia(uid: string, saveId: string): Promise<void> {
  await backendJson('/media/delete', {
    method: 'POST',
    body: { uid, saveId },
  })
}

export async function listManagedUsage(uid?: string): Promise<any[]> {
  const result = await backendJson<{ usage: any[] }>('/managed-usage/list', {
    method: 'POST',
    body: { uid },
  })
  return result.usage ?? []
}

export async function writeManagedUsage(entry: unknown): Promise<void> {
  await backendJson('/managed-usage/write', {
    method: 'POST',
    body: { entry },
  })
}

export async function listMarketplaceReceipts(uid: string): Promise<any[]> {
  const result = await backendJson<{ receipts: any[] }>('/receipts/list', {
    method: 'POST',
    body: { uid },
  })
  return result.receipts ?? []
}

export async function writeMarketplaceReceipt(receipt: unknown): Promise<void> {
  await backendJson('/receipts/write', {
    method: 'POST',
    body: { receipt },
  })
}

export async function readBillingAuditSnapshot(): Promise<{ events: any[]; customerCount: number }> {
  return backendJson<{ events: any[]; customerCount: number }>('/billing/audit')
}
